import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TablePagination,
  CircularProgress,
  Box,
  Grid,
  TableSortLabel,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { configApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
  },
  table: {
    minWidth: 400,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  chip: {
    minWidth: 70,
  },
  participationChip: {
    backgroundColor: '#4CAF50',
    color: 'white',
    '&.false': {
      backgroundColor: '#f44336',
    },
  },
  clarityChip: {
    backgroundColor: '#2196F3',
    color: 'white',
    '&.false': {
      backgroundColor: '#f44336',
    },
  },
  scoreCell: {
    fontWeight: 'bold',
  },
  headerCell: {
    fontWeight: 'bold',
    backgroundColor: theme.palette.grey[100],
  },
  sortableHeader: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.grey[200],
    },
  },
  sortLabel: {
    width: '100%',
    justifyContent: 'flex-start',
    '&.MuiTableSortLabel-root': {
      width: '100%',
    },
    '&.center': {
      justifyContent: 'center',
    },
    '&.right': {
      justifyContent: 'flex-end',
    },
  },
  reasonCell: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.grey[50],
    },
  },
  reasonText: {
    maxWidth: 130,
  },
  tooltip: {
    fontSize: '0.875rem',
    maxWidth: 300,
    lineHeight: 1.4,
  },
  dateRangeContainer: {
    padding: theme.spacing(2),
    paddingBottom: 0,
  },
}));

interface QualityMetric {
  name: string;
  tribe: string;
  target_name: string;
  project_name: string;
  participation: boolean;
  participation_score: number;
  clarity: boolean;
  clarity_score: number;
  created_at: string;
  reason: string;
}

interface QualityMetricListProps {
  title?: string;
  isUser?: boolean;
  startDate?: string; // ISO date string (YYYY-MM-DD)
  endDate?: string;   // ISO date string (YYYY-MM-DD)
}

type SortableField = keyof QualityMetric;
type SortOrder = 'asc' | 'desc';

type SummaryEntry = {
  target_name: string;
  count: number;
  clarity_score: number;
  participation_score: number;
};

export const QualityMetricList: React.FC<QualityMetricListProps> = ({
  title = 'Quality Metrics Entries',
  isUser = false,
  startDate: initialStartDate,
  endDate: initialEndDate,
}) => {
  const classes = useStyles();
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);

  const [metrics, setMetrics] = useState<QualityMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortField, setSortField] = useState<SortableField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const today = new Date();
  const defaultStartDate = new Date();
  defaultStartDate.setDate(today.getDate() - 7); // Set to 7 days before today
  const [startDate, setStartDate] = useState<Date>(initialStartDate ? new Date(initialStartDate) : defaultStartDate);
  const [endDate, setEndDate] = useState<Date>(initialEndDate ? new Date(initialEndDate) : today);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        const baseUrl = configApi.getString('backend.baseUrl');
        const { token } = await identityApi.getCredentials();

        const params = new URLSearchParams({
          tribe: isUser ? '' : entity.metadata.name,
          name: isUser ? entity.metadata.name : '',
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0],
        });

        const response = await fetch(`${baseUrl}/api/quality-metric/metrics?${params.toString()}`, {
          method: 'GET',
          headers: token
            ? {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
            : {},
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: QualityMetric[] = await response.json();
        setMetrics(data);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching metrics:', err);
        setError('Failed to load quality metrics');
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [configApi, identityApi, entity.metadata.name, isUser, startDate, endDate]);

  const handleSort = (field: SortableField) => {
    const isAsc = sortField === field && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortField(field);
    setPage(0);
  };

  const sortedMetrics = [...metrics].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    if (aValue === null && bValue === null) return 0;
    if (aValue === null) return sortOrder === 'asc' ? -1 : 1;
    if (bValue === null) return sortOrder === 'asc' ? 1 : -1;

    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
    }
    if (typeof bValue === 'string') {
      bValue = bValue.toLowerCase();
    }

    if (sortField === 'created_at') {
      aValue = new Date(aValue as string).getTime();
      bValue = new Date(bValue as string).getTime();
    }

    if (aValue < bValue) {
      return sortOrder === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortOrder === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const paginatedMetrics = sortedMetrics.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const SortableTableCell: React.FC<{
    field: SortableField;
    children: React.ReactNode;
    align?: 'left' | 'center' | 'right';
  }> = ({ field, children, align = 'left' }) => (
    <TableCell
      className={`${classes.headerCell} ${classes.sortableHeader}`}
      align={align}
      style={{ padding: 0 }}
    >
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortOrder : 'asc'}
        onClick={() => handleSort(field)}
        className={`${classes.sortLabel} ${align}`}
        style={{
          padding: '16px',
          minHeight: '57px',
          display: 'flex',
          alignItems: 'center',
          width: '100%'
        }}
      >
        {children}
      </TableSortLabel>
    </TableCell>
  );

  const renderReasonCell = (reason: string) => {
    if (!reason) {
      return <TableCell>-</TableCell>;
    }

    const isReasonTruncated = reason.length > 30;
    const displayReason = isReasonTruncated ? `${reason.substring(0, 30)}...` : reason;

    if (isReasonTruncated) {
      return (
        <TableCell className={classes.reasonCell}>
          <Tooltip
            title={reason}
            classes={{ tooltip: classes.tooltip }}
            arrow
            placement="top"
          >
            <Typography variant="body2" className={classes.reasonText} style={{ maxWidth: 130 }}>
              {displayReason}
            </Typography>
          </Tooltip>
        </TableCell>
      );
    }

    return (
      <TableCell>
        <Typography variant="body2" className={classes.reasonText} style={{ maxWidth: 130 }}>
          {displayReason}
        </Typography>
      </TableCell>
    );
  };

  const renderFilterControls = () => (
    <Box className={classes.dateRangeContainer}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Start Date"
            type="date"
            value={startDate.toISOString().split('T')[0]}
            onChange={(e) => setStartDate(new Date(e.target.value))}
            InputLabelProps={{ shrink: true }}
            fullWidth
            variant="outlined"
            inputProps={{ max: endDate.toISOString().split('T')[0] }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="End Date"
            type="date"
            value={endDate.toISOString().split('T')[0]}
            onChange={(e) => setEndDate(new Date(e.target.value))}
            InputLabelProps={{ shrink: true }}
            fullWidth
            variant="outlined"
            inputProps={{
              min: startDate.toISOString().split('T')[0],
              max: today.toISOString().split('T')[0]
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );

  if (loading) {
    return (
      <Card className={classes.root}>
        <CardHeader title={title} subheader={`of ${entity.metadata.name}`} action={renderFilterControls()} />
        <CardContent>
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={classes.root}>
        <CardHeader title={title} subheader={`of ${entity.metadata.name}`} action={renderFilterControls()} />
        <CardContent>
          <Typography color="error">{error}</Typography>
        </CardContent>
      </Card>
    );
  }

  if (metrics.length === 0) {
    return (
      <Card className={classes.root}>
        <CardHeader title={title} subheader={`of ${entity.metadata.name}`} action={renderFilterControls()} />
        <CardContent>
          <Typography variant="body2" color="textSecondary">
            No quality metrics found.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const groupAndSummarize = (data: QualityMetric[]): SummaryEntry[] => {
    const grouped: Record<string, SummaryEntry> = {};

    data.forEach((entry) => {
      const key = entry.target_name;
      if (!grouped[key]) {
        grouped[key] = {
          target_name: key,
          count: 0,
          clarity_score: 0,
          participation_score: 0,
        };
      }

      grouped[key].count += 1;
      grouped[key].clarity_score += entry.clarity_score;
      grouped[key].participation_score += entry.participation_score;
    });

    // Convert object to array and sort by total score (descending)
    return Object.values(grouped).sort((a, b) => {
      const totalA = a.clarity_score + a.participation_score;
      const totalB = b.clarity_score + b.participation_score;
      return totalB - totalA;
    });
  };

  return (
    <Card className={classes.root}>
      <CardHeader
        title={title}
        subheader={`of ${entity.metadata.name}`}
        action={renderFilterControls()}
      />

      <CardContent>
        <h3>Summary</h3>
        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Clarity</TableCell>
                <TableCell>Participation</TableCell>
                <TableCell>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupAndSummarize(paginatedMetrics).map((item) => (
                <TableRow key={item.target_name}>
                  <TableCell>{item.target_name}</TableCell>
                  <TableCell align="center">{item.clarity_score}</TableCell>
                  <TableCell align="center">{item.participation_score}</TableCell>
                  <TableCell align="center">{item.clarity_score + item.participation_score}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>

      <CardContent>
        <h3>Entries</h3>
        <TableContainer component={Paper} elevation={0}>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <SortableTableCell field="name">
                  Reviewer
                </SortableTableCell>
                <SortableTableCell field="target_name">
                  Team Member
                </SortableTableCell>
                <SortableTableCell field="participation_score" align="center">
                  Participation
                </SortableTableCell>
                <SortableTableCell field="clarity_score" align="center">
                  Clarity
                </SortableTableCell>
                <SortableTableCell field="project_name">
                  Project
                </SortableTableCell>
                <SortableTableCell field="created_at">
                  Date
                </SortableTableCell>
                <SortableTableCell field="reason">
                  Reason
                </SortableTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedMetrics.map((metric, index) => (
                <TableRow key={`${metric.name}-${metric.target_name}-${metric.created_at}-${index}`}>
                  <TableCell>{metric.name}</TableCell>
                  <TableCell>{metric.target_name}</TableCell>
                  <TableCell align="center" className={classes.scoreCell}>
                    {metric.participation_score}
                  </TableCell>
                  <TableCell align="center" className={classes.scoreCell}>
                    {metric.clarity_score}
                  </TableCell>
                  <TableCell>
                    <Typography style={{ maxWidth: 130 }}>{metric.project_name || '-'}</Typography>
                  </TableCell>
                  <TableCell>{formatDate(metric.created_at)}</TableCell>
                  {renderReasonCell(metric.reason)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={metrics.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </CardContent>
    </Card>
  );
};

export default QualityMetricList;
